import { useEffect, useState } from 'react'
import { Banknote, CreditCard, Landmark, Loader2, Smartphone } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { addDays, todayISO } from '../lib/dates'
import { Button, Field, Input, Sheet, cx } from '../ui/kit'
import { QrCode } from '../ui/QrCode'
import type { Invoice, Payment } from '../data/model'

/**
 * Kassieren.
 *
 * WARUM ES DEN KNOPF „Als bezahlt markieren" ERSETZT: der buchte stillschweigend
 * „heute, per Überweisung", ganz gleich was tatsächlich passiert ist. Für die Hälfte
 * der Fälle war das falsch — bar an der Tür, Karte am Handy, die Überweisung vom
 * Dienstag, eingetragen am Freitag. Und auf der Rechnung stand nachher nichts davon.
 *
 * WARUM DER WEG UND DAS DATUM AUF DEN BELEG GEHÖREN: ein Beleg, der einen offenen
 * Betrag ausweist, ist eine Zahlungsaufforderung; ein Beleg mit „Betrag dankend
 * erhalten am 27.07.2026, bar" ist eine Quittung. Der Gast braucht das zweite, wenn er
 * bezahlt hat — sonst bekommt er zwei Wochen später eine Mahnung für etwas, das er in
 * bar bezahlt hat, und niemand kann beweisen, wann.
 *
 * WARUM DER QR-CODE: der Gast steht vor einem, das Handy in der Hand. Einen Link
 * abtippen wird er nicht, und in einer Mail suchen will er auch nicht. Er scannt, zahlt
 * auf dem Stripe-Konto DES VERMIETERS, und der Vermieter sieht es in derselben Maske.
 */
export default function CollectPayment({
  invoice, open, onClose,
}: { invoice: Invoice | null; open: boolean; onClose: () => void }) {
  const { lang, formatMoney } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const [method, setMethod] = useState<Payment['method']>('cash')
  const [amount, setAmount] = useState('')
  /**
   * Leer heißt „ich weiß es noch nicht" — und das ist bei einer Überweisung der
   * Normalfall. Siehe `needsValueDate` weiter unten.
   */
  const [receivedOn, setReceivedOn] = useState(todayISO())
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const paid = invoice?.booking_id
    ? store.paidFor(invoice.booking_id)
    : invoice?.paid_amount ?? 0
  const outstanding = invoice
    ? Math.round((invoice.total_gross - paid) * 100) / 100
    : 0

  useEffect(() => {
    if (!open || !invoice) return
    setAmount(String(Math.max(0, outstanding)))
    setReceivedOn(todayISO())
    setMethod('cash')
    setQrUrl(null)
    setError(null)
  }, [open, invoice?.id])

  if (!invoice) return null

  const connected = Boolean(store.db.tenant.stripe_account_id)

  /*
   * Bar, Karte und Stripe sind Vorgänge am Tresen: das Geld ist da, während dieses
   * Blatt offen ist, und „heute" stimmt.
   *
   * Eine ÜBERWEISUNG ist etwas völlig anderes. Sie passiert nicht hier, sondern beim
   * Gast zu Hause, irgendwann. Der Vermieter erfährt davon erst, wenn er sie auf dem
   * Kontoauszug sieht — und dann liegt der Eingang oft Tage zurück. Das Blatt hat
   * bisher trotzdem „heute" vorgeschlagen und damit ein Datum angeboten, das fast
   * immer falsch war: es hätte die Rechnung an einem Tag als bezahlt ausgewiesen, an
   * dem noch kein Geld da war, und in der Auswertung nach Zahlungseingang (siehe
   * BookkeepingView) im falschen Monat gelandet.
   *
   * Deshalb: bei einer Überweisung wird das Datum verlangt statt vorgeschlagen, und
   * es gibt einen zweiten Weg hinaus — „warte noch auf den Eingang", der nichts
   * bucht. Wer nichts gesehen hat, soll auch nichts buchen.
   */
  const needsValueDate = method === 'bank_transfer'
  const dateMissing = needsValueDate && !receivedOn

  const METHODS: { key: Payment['method']; icon: typeof Banknote; label: string }[] = [
    { key: 'cash', icon: Banknote, label: de ? 'Bar' : 'Cash' },
    { key: 'card', icon: CreditCard, label: de ? 'Karte' : 'Card' },
    { key: 'bank_transfer', icon: Landmark, label: de ? 'Überweisung' : 'Transfer' },
    { key: 'stripe', icon: Smartphone, label: 'Stripe' },
  ]

  const book = async () => {
    const value = Math.round(Number(amount) * 100) / 100
    if (!(value > 0)) { setError(de ? 'Betrag fehlt.' : 'Amount missing.'); return }
    setBusy(true); setError(null)
    await store.addPayment({
      booking_id: invoice.booking_id,
      invoice_id: invoice.id,
      // Der Restbetrag nach einer Anzahlung ist die Schlusszahlung. Die Anzahlung
      // selbst wird an der Buchung gebucht, nicht hier.
      kind: 'balance',
      method,
      amount: value,
      currency: invoice.currency,
      received_on: receivedOn,
    })
    setBusy(false)
    onClose()
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={de ? 'Zahlung erfassen' : 'Record a payment'}
      footer={
        <>
          {/* Bei einer Überweisung ist „nichts buchen" der häufigere Ausgang, nicht
              das Abbrechen aus Versehen — deshalb bekommt er hier einen eigenen
              Namen statt „Abbrechen". */}
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {needsValueDate
              ? (de ? 'Noch nicht eingegangen' : 'Not received yet')
              : (de ? 'Abbrechen' : 'Cancel')}
          </Button>
          <Button variant="primary" className="flex-1" disabled={busy || dateMissing}
            onClick={book}>
            {busy && <Loader2 size={15} className="animate-spin" />}
            {needsValueDate
              ? (de ? 'Eingang buchen' : 'Book the receipt')
              : (de ? 'Zahlung buchen' : 'Book payment')}
          </Button>
        </>
      }
    >
      <div className="bg-surface-2 rounded-2xl p-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted">{invoice.number ?? (de ? 'Entwurf' : 'Draft')}</span>
          <span className="tabular-nums">{formatMoney(invoice.total_gross)}</span>
        </div>
        {paid > 0 && (
          <div className="flex justify-between text-muted">
            <span>{de ? 'bereits bezahlt' : 'already paid'}</span>
            <span className="tabular-nums">−{formatMoney(paid)}</span>
          </div>
        )}
        <div className="flex justify-between font-medium mt-1 pt-1 border-t border-border">
          <span>{de ? 'Offen' : 'Outstanding'}</span>
          <span className="tabular-nums">{formatMoney(outstanding)}</span>
        </div>
      </div>

      <Field label={de ? 'Wie wurde bezahlt?' : 'How was it paid?'}>
        <div className="grid grid-cols-4 gap-2">
          {METHODS.map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => {
                setMethod(key)
                // Beim Wechsel auf Überweisung wird das Datum geleert: es muss aus
                // dem Kontoauszug kommen und darf nicht aus einem Vorschlag
                // stehenbleiben, den niemand geprüft hat. Zurück auf Tresenzahlung
                // ist „heute" wieder richtig.
                setReceivedOn(key === 'bank_transfer' ? '' : todayISO())
              }}
              className={cx(
                'flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs border',
                method === key
                  ? 'border-accent bg-accent-soft text-accent font-medium'
                  : 'border-border bg-surface-2 text-muted',
              )}
            >
              <Icon size={17} />
              {label}
            </button>
          ))}
        </div>
      </Field>

      {/* Stripe ist der einzige Weg, bei dem hier noch etwas zu tun ist: der Gast muss
          zahlen, bevor gebucht werden kann. Deshalb steht der Code direkt darunter. */}
      {method === 'stripe' && (
        <div className="rounded-2xl bg-surface-2 p-3 space-y-3">
          {!connected ? (
            <p className="text-sm text-muted">
              {de
                ? 'Dafür muss in den Einstellungen ein eigenes Stripe-Konto verbunden sein. Das Geld geht direkt dorthin, nie über uns.'
                : 'This needs your own Stripe account connected in Settings. The money goes straight there, never through us.'}
            </p>
          ) : qrUrl ? (
            <div className="flex flex-col items-center gap-2">
              <div className="bg-white p-3 rounded-xl">
                <QrCode value={qrUrl} size={168} />
              </div>
              <p className="text-xs text-muted text-center">
                {de
                  ? 'Der Gast scannt und zahlt auf Ihrem Stripe-Konto. Danach hier die Zahlung buchen.'
                  : 'The guest scans and pays into your Stripe account. Then book the payment here.'}
              </p>
              <button
                className="text-xs underline underline-offset-2 text-muted"
                onClick={() => { void navigator.clipboard?.writeText(qrUrl) }}
              >
                {de ? 'Link kopieren' : 'Copy the link'}
              </button>
            </div>
          ) : (
            <Button variant="secondary" className="w-full" disabled={busy}
              onClick={async () => {
                setBusy(true); setError(null)
                const url = await store.guestPaymentLink(invoice.id)
                if (!url) setError(de ? 'Der Zahlungslink kam nicht zustande.' : 'The payment link failed.')
                setQrUrl(url)
                setBusy(false)
              }}>
              {busy && <Loader2 size={15} className="animate-spin" />}
              {de ? 'QR-Code zum Zahlen erzeugen' : 'Create a QR code to pay'}
            </Button>
          )}
        </div>
      )}

      {/* Der Satz steht VOR den Feldern, nicht als Fußnote darunter: er ändert, was
          die Felder bedeuten, und wer ihn erst hinterher liest, hat schon gebucht. */}
      {needsValueDate && (
        <div className="rounded-2xl bg-info-soft text-info p-3 space-y-2 text-sm">
          <p>
            {de
              ? 'Eine Überweisung sehen Sie erst auf dem Kontoauszug. Buchen Sie sie erst dann — mit dem Tag, an dem das Geld eingegangen ist.'
              : 'A transfer only shows up on your bank statement. Book it then, with the day the money actually arrived.'}
          </p>
          {!receivedOn && (
            <div className="flex gap-2 flex-wrap">
              {/* Der Kontoauszug wird meist ein paar Tage später angeschaut, deshalb
                  liegen die Abkürzungen in der Vergangenheit und nicht in der Zukunft. */}
              {[0, 1, 2, 3].map((back) => (
                <button
                  key={back}
                  className="px-2.5 py-1 rounded-lg bg-surface text-xs"
                  onClick={() => setReceivedOn(addDays(todayISO(), -back))}
                >
                  {back === 0 ? (de ? 'heute' : 'today')
                    : back === 1 ? (de ? 'gestern' : 'yesterday')
                    : (de ? `vor ${back} Tagen` : `${back} days ago`)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'Betrag' : 'Amount'}>
          <Input type="number" step="0.01" value={amount}
            onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field
          label={needsValueDate
            ? (de ? 'Eingegangen am' : 'Received on')
            : (de ? 'Bezahlt am' : 'Paid on')}
          hint={needsValueDate
            ? (de ? 'Laut Kontoauszug' : 'From your bank statement')
            : (de ? 'Der Tag des Eingangs, nicht der Eintragung' : 'The day it arrived, not the day you typed it')}
        >
          {/* Kein Datum in der Zukunft: Geld, das noch nicht da ist, wird nicht
              gebucht — genau der Fehler, den dieses Blatt vermeiden soll. */}
          <Input type="date" value={receivedOn} max={todayISO()}
            onChange={(e) => setReceivedOn(e.target.value)} />
        </Field>
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      <p className="text-xs text-muted">
        {de
          ? 'Weg und Tag stehen danach auf der Rechnung — damit sie als Quittung taugt.'
          : 'The method and date then appear on the invoice, so it works as a receipt.'}
      </p>
    </Sheet>
  )
}
